/*
 * Badge: a small label (plan name, "Beta", counts). For states use StatusPill.
 *   <Badge tone="brand">Pro</Badge>   <Badge tone="neutral" mono>1.4.2</Badge>
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'ai' | 'indigo'

export const TONE_CLASSES: Record<
  Tone,
  { pill: string; dot: string; soft: string; text: string; bar: string }
> = {
  neutral: {
    pill: 'bg-control text-muted',
    dot: 'bg-subtle',
    soft: 'bg-surface-quiet',
    text: 'text-muted',
    bar: 'bg-subtle',
  },
  brand: {
    pill: 'bg-brand-50 text-brand-700',
    dot: 'bg-brand-600',
    soft: 'bg-brand-50',
    text: 'text-brand-700',
    bar: 'bg-brand-600',
  },
  success: {
    pill: 'bg-teal-50 text-teal-700',
    dot: 'bg-teal-400',
    soft: 'bg-teal-50',
    text: 'text-teal-700',
    bar: 'bg-teal-600',
  },
  warning: {
    pill: 'bg-warm-50 text-warm-700',
    dot: 'bg-warm-500',
    soft: 'bg-warm-50',
    text: 'text-warm-700',
    bar: 'bg-warm-500',
  },
  danger: {
    pill: 'bg-danger-50 text-danger-700',
    dot: 'bg-danger-600',
    soft: 'bg-danger-50',
    text: 'text-danger-700',
    bar: 'bg-danger-600',
  },
  ai: {
    pill: 'bg-teal-50 text-teal-700',
    dot: 'bg-teal-600',
    soft: 'bg-teal-50',
    text: 'text-teal-700',
    bar: 'bg-teal-600',
  },
  indigo: {
    pill: 'bg-indigo-50 text-indigo-700',
    dot: 'bg-indigo-600',
    soft: 'bg-indigo-50',
    text: 'text-indigo-700',
    bar: 'bg-indigo-600',
  },
}

export function Badge({
  tone = 'neutral',
  mono,
  icon,
  children,
  className,
}: {
  tone?: Tone
  mono?: boolean
  icon?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-12 font-semibold whitespace-nowrap [&_svg]:size-3.5',
        TONE_CLASSES[tone].pill,
        mono && 'font-mono font-medium ltr-island',
        className,
      )}
    >
      {icon}
      {children}
    </span>
  )
}

/** Marks a link to a page that is not published yet (see PLACEHOLDER_PAGES in src/lib/site.ts). */
export function SoonBadge({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  return (
    <Badge tone="neutral" className={cn('h-5 px-2 text-[11px]', className)}>
      {t('placeholder.soon')}
    </Badge>
  )
}
